import AddIcon from "@mui/icons-material/Add";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import CloseIcon from "../../../common/icons/CloseIcon";
import TextField from "../../../common/TextField";
import type { CustomerEntry } from '../../../../services/types/vacancy.types';
import { AsyncAutocomplete } from "../../../common/AsyncAutocomplete";
import { API_HEADERS } from '../../../../constants/apiHeaders';
import api from "../../../../services/httpClient";
import { CUSTOMER_ENDPOINTS } from '../../../../services/endpoints/customer.endpoints';

interface VacancyCustomerCardProps {
  customers: CustomerEntry[];
  onAddCustomer: () => void;
  errors: Record<string, string>;
  type: "add" | "edit" | "view";
  setErrors: (errors: Record<string, string>) => void;
  onRemoveCustomer: (id: string) => void;
  setCustomers: (customers: CustomerEntry[]) => void;
}

const VacancyCustomerCard: React.FC<VacancyCustomerCardProps> = ({
  type,
  customers,
  errors,
  setErrors,
  onAddCustomer,
  onRemoveCustomer,
  setCustomers,
}) => {
  const handleLoadCustomers = async (query: string) => {
    const response = await api.get<{
      data: { id: string; name: string; countryCode: string }[];
    }>(CUSTOMER_ENDPOINTS.customers, {
      headers: {
        ...(type === 'add' ? API_HEADERS.VACANCY_ADD_CUSTOMER_SEARCH : API_HEADERS.VACANCY_EDIT_CUSTOMER_SEARCH),
      },
    });
    const data = response.data.data;
    return data.map(
      (customer: { id: string; name: string; countryCode: string }) => ({
        label: customer.name,
        value: customer.id,
        countryCode: customer.countryCode,
      }),
    );
  };

  return (
    <Box className="edit-section">
      {/* Title Row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography className="edit-section-title-no-capitalize">
          Customer(s)*
        </Typography>
        {type !== "view" && customers.length < 4 && (
          <IconButton
            className="edit-section-add-button"
            onClick={onAddCustomer}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {/* Customer Cards */}
      {customers.map((customer, index) => (
        <Box key={customer.rowId} className="edit-sub-section" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 7 }}>
            <Box>
              <AsyncAutocomplete
                width="360px"
                label="Customer Name"
                error={!!errors[`customer_${index}_id`]}
                helperText={errors[`customer_${index}_id`]}
                value={
                  customer.id
                    ? {
                        value: customer.id,
                        label: `${customer.name} - ${customer.countryCode}`,
                        countryCode: customer.countryCode,
                      }
                    : null
                }
                loadOptions={handleLoadCustomers}
                onChange={(value) => {
                  setErrors({ ...errors, [`customer_${index}_id`]: '' });
                  setCustomers(
                    customers.map((customer, i) =>
                      i === index
                        ? {
                            ...customer,
                            id: value?.value || '',
                            name: value?.label || '',
                            countryCode: (value?.countryCode as string) || '',
                          }
                        : customer,
                    ),
                  );
                }}
              />
            </Box>
            <Box>
              <TextField
                label="No. Of Vacancies"
                sx={{ width: "360px" }}
                type="number"
                error={!!errors[`customer_${index}_numberOfVacancies`]}
                helperText={errors[`customer_${index}_numberOfVacancies`]}
                value={customer.numberOfVacancies}
                onChange={(e) => {
                  setErrors({
                    ...errors,
                    [`customer_${index}_numberOfVacancies`]: '',
                  });
                  setCustomers(
                    customers.map((customer, i) =>
                      i === index
                        ? { ...customer, numberOfVacancies: e.target.value }
                        : customer,
                    ),
                  );
                }}
                name={`customers[${customer.id}].numberOfVacancies`}
              />
            </Box>
            <Box>
              <TextField
                label="Revenue per resource"
                sx={{ width: "360px" }}
                type="number"
                error={!!errors[`customer_${index}_revenuePerResource`]}
                helperText={errors[`customer_${index}_revenuePerResource`]}
                value={customer.revenuePerResource}
                onChange={(e) => {
                  setErrors({
                    ...errors,
                    [`customer_${index}_revenuePerResource`]: '',
                  });
                  setCustomers(
                    customers.map((customer, i) =>
                      i === index
                        ? { ...customer, revenuePerResource: e.target.value }
                        : customer,
                    ),
                  );
                }}
                name={`customers[${customer.id}].revenuePerResource`}
              />
            </Box>
            {type !== "view" && customers.length > 1 && (
              <IconButton
                onClick={() => {
                  setErrors({ ...errors, [`customer_${index}_id`]: '', [`customer_${index}_numberOfVacancies`]: '', [`customer_${index}_revenuePerResource`]: '' });
                  onRemoveCustomer(customer.rowId);
                }}
                className="edit-section-close-button"
                sx={{
                  marginBottom:
                    !!errors[`customer_${index}_revenuePerResource`] ||
                    !!errors[`customer_${index}_numberOfVacancies`] ||
                    !!errors[`customer_${index}_id`]
                      ? 2
                      : 0,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default VacancyCustomerCard;
